import {TextField} from '@common/ui/forms/input-field/text-field/text-field';
import {LandingPageContent} from '@app/landing/landing-page-content';
import {Button} from '@common/ui/buttons/button';
import {useTrans} from '@common/i18n/use-trans';
import {useMutation, useQuery} from '@tanstack/react-query';
import {toast} from '@common/ui/toast/toast';
import {message} from '@common/i18n/message';
import {apiClient} from '@common/http/query-client';
import {showHttpErrorToast} from '@common/utils/http/show-http-error-toast';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {useRef, useState} from 'react';
import {urlIsValid} from '@app/dashboard/links/utils/url-is-valid';
import {Link} from '@app/dashboard/links/link';
import {Trans} from '@common/i18n/trans';
import {flushSync} from 'react-dom';
import useClipboard from 'react-use-clipboard';
import {useSettings} from '@common/core/settings/use-settings';
import {useRecaptcha} from '@common/recaptcha/use-recaptcha';
import {useLightThemeVariables} from '@common/ui/themes/use-light-theme-variables';
import {Select, Option} from '@common/ui/forms/select/select';
import {PrimitiveValue} from '@common/ui/forms/listbox/types';
import {LinkIcon} from '@common/icons/material/Link';
import {ContentCopyIcon} from '@common/icons/material/ContentCopy';
import {OpenInNewIcon} from '@common/icons/material/OpenInNew';
import {QrCodeIcon} from '@common/icons/material/QrCode';
import {ProgressCircle} from '@common/ui/progress/progress-circle';
import {openDialog} from '@common/ui/overlays/store/dialog-store';
import {QrCodeModal} from './qr-code-modal';

interface LandingPageNewLinkFormProps {
  content: LandingPageContent;
}
export function LandingPageNewLinkForm({content}: LandingPageNewLinkFormProps) {
  const {trans} = useTrans();
  const lightThemeVars = useLightThemeVariables();
  const {verify, isVerifying} = useRecaptcha('link_creation');
  const {
    links: {default_type},
  } = useSettings();
  const inputRef = useRef<HTMLInputElement>(null);
  const [longUrl, setLongUrl] = useState('');
  const [shortUrl, setShortUrl] = useState('');
  const [linkId, setLinkId] = useState<number | null>(null);
  const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null);
  const [, copyToClipboard] = useClipboard(shortUrl, {
    successDuration: 100,
  });

  const openQrModal = () => {
    openDialog(QrCodeModal, {
      shortUrl: shortUrl,
      linkId: linkId,
    });
  };

  // Fetch public domains
  const {data: domainsData, isLoading, error} = useQuery({
    queryKey: ['public-domains'],
    queryFn: () => fetchPublicDomains(),
  });

  const {data: configData} = useQuery({
    queryKey: ['app-config'],
    queryFn: () => fetchAppConfig(),
  });




  const createLink = useMutation({
    mutationFn: () => {
      const createPayload: Partial<Link> = {
        long_url: longUrl,
        type: default_type || 'direct',
        domain_id: selectedDomainId || undefined,
      };
      return fetch('/api/public/link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(createPayload),
      }).then(async r => {
        const data = await r.json();
        if (!r.ok) {
          throw new Error(data.error || 'Failed to create link');
        }
        return data;
      });
    },
    onSuccess: response => {
      toast.positive(trans(message('Link shortened')));
      flushSync(() => {
        setShortUrl(response.link.short_url);
        setLinkId(response.link.id);
      });
      inputRef.current?.select();
    },
    onError: err => {
      // Show the specific error message from server if available
      if (err.message && err.message !== 'Failed to create link') {
        toast.danger(err.message);
      } else {
        showHttpErrorToast(
          err,
          message('Could not shorten link. Please try again later'),
          'long_url',
        );
      }
    },
  });

  return (
    <form
      className="mt-60 w-full md:mt-80"
      onSubmit={async e => {
        e.preventDefault();

        if (createLink.isPending || isVerifying) return;

        // Input is in "copy" mode, copy shortened link to clipboard on submit
        if (shortUrl) {
          flushSync(() => {
            copyToClipboard();
            setShortUrl('');
            setLongUrl('');
            setLinkId(null);
            setSelectedDomainId(null);
          });
          toast.positive(message('Copied link to clipboard'));
          return;
        }

        // input is in "shorten" mode, shorten link on submit
        if (!urlIsValid(longUrl)) {
          toast.danger(message('This url is invalid.'));
        } else {
          const isValid = await verify();
          if (isValid) {
            createLink.mutate();
          }
        }
      }}
    >
  <div className="shorten-link-form-row">
        {/* Desktop Layout: Input with buttons inside */}
        <div className="hidden sm:flex flex-1 gap-4">
          <TextField
            inputRef={inputRef}
            background="bg-white"
            inputRadius="rounded-full"
            style={lightThemeVars}
            size="lg"
            placeholder={shortUrl ? trans(message('Click to copy short link')) : trans(message('Paste your long link here'))}
            value={shortUrl || longUrl}
            onChange={e => {
              setLongUrl(e.target.value);
              if (shortUrl) {
                setShortUrl(''); // Clear short URL when user types new URL
                setLinkId(null); // Clear link ID as well
              }
            }}
            onClick={() => {
              if (shortUrl) {
                copyToClipboard();
                toast.positive(trans(message('Link copied to clipboard!')));
              }
            }}
            className={`flex-1 ${shortUrl ? 'cursor-pointer select-all' : ''}`}
            startAdornment={
              shortUrl ? (
                <ContentCopyIcon className="text-primary icon-md ml-10 mr-4" />
              ) : (
                <LinkIcon className="text-muted icon-md ml-10 mr-4" />
              )
            }
            endAdornment={
              shortUrl ? (
                <div className="flex gap-2 items-center justify-end min-w-[240px] mr-4" style={{zIndex: 20, position: 'relative'}}>
                  <Button
                    variant="raised"
                    color="primary"
                    size="md"
                    radius="rounded-full"
                    className="cursor-pointer shadow-md"
                    style={{pointerEvents: 'auto', zIndex: 25}}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(shortUrl, '_blank');
                    }}
                  >
                    <OpenInNewIcon />
                  </Button>
                  <Button
                    variant="raised"
                    color="primary"
                    size="md"
                    radius="rounded-full"
                    className="cursor-pointer shadow-md"
                    style={{pointerEvents: 'auto', zIndex: 25}}
                    onClick={(e) => {
                      e.stopPropagation();
                      openQrModal();
                    }}
                  >
                    <QrCodeIcon />
                  </Button>
                  <Button
                    variant="raised"
                    color="primary"
                    size="md"
                    radius="rounded-full"
                    className="cursor-pointer whitespace-nowrap shadow-md px-6"
                    style={{pointerEvents: 'auto', zIndex: 25}}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Reset for new link
                      setShortUrl('');
                      setLongUrl('');
                      setSelectedDomainId(null);
                    }}
                  >
                    {trans(message('New Link'))}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="raised"
                  color="primary"
                  size="md"
                  radius="rounded-full"
                  className="mr-4 cursor-pointer shadow-md px-8"
                  style={{pointerEvents: 'auto', zIndex: 20, position: 'relative'}}
                  disabled={createLink.isPending || !longUrl.trim()}
                  onClick={(e) => {
                    e.stopPropagation();
                    createLink.mutate();
                  }}
                >
                  {createLink.isPending ? (
                    <ProgressCircle size="sm" isIndeterminate />
                  ) : (
                    trans(message('Shorten'))
                  )}
                </Button>
              )
            }
          />
          
          {!shortUrl && domainsData?.domains && domainsData.domains.length > 0 && (
            <div className="flex-shrink-0">
              <Select
                background="bg-white"
                style={lightThemeVars}
                size="lg"
                selectionMode="single"
                placeholder={configData?.defaultDomain || 'Loading...'}
                selectedValue={selectedDomainId}
                onSelectionChange={(value: PrimitiveValue) => setSelectedDomainId(value as number | null)}
                className="min-w-150 shadow-lg rounded-full border-0"
                inputRadius="rounded-full"
              >
                <Option value={null}>{configData?.defaultDomain || 'Default Domain'}</Option>
                {domainsData?.domains
                  ?.filter((domain: any) => {
                    const domainHost = domain.host.replace(/^https?:\/\//, '');
                    return domainHost !== configData?.defaultDomain;
                  })
                  ?.map((domain: any) => (
                    <Option key={domain.id} value={domain.id}>
                      {domain.host.replace(/^https?:\/\//, '')}
                    </Option>
                  )) || []}
              </Select>
            </div>
          )}
        </div>

        {/* Mobile Layout: Input and buttons below */}
        <div className="flex flex-col sm:hidden w-full gap-4">
          {/* Input Field */}
          <div className="w-full">
            <TextField
              inputRef={inputRef}
              background="bg-white"
              inputRadius="rounded-full"
              style={lightThemeVars}
              size="lg"
              placeholder={shortUrl ? trans(message('Click to copy short link')) : trans(message('Paste your long link here'))}
              value={shortUrl || longUrl}
              onChange={e => {
                setLongUrl(e.target.value);
                if (shortUrl) {
                setShortUrl(''); // Clear short URL when user types new URL
                setLinkId(null); // Clear link ID as well
              }
              }}
              onClick={() => {
                if (shortUrl) {
                  copyToClipboard();
                  toast.positive(trans(message('Link copied to clipboard!')));
                }
              }}
              className={`w-full ${shortUrl ? 'cursor-pointer select-all' : ''}`}
              startAdornment={
                shortUrl ? (
                  <ContentCopyIcon className="text-primary icon-md ml-10 mr-4" />
                ) : (
                  <LinkIcon className="text-muted icon-md ml-10 mr-4" />
                )
              }
            />
          </div>

          {/* Domain Selector for Mobile */}
          {!shortUrl && domainsData?.domains && domainsData.domains.length > 0 && (
            <div className="w-full">
              <Select
                background="bg-white"
                style={lightThemeVars}
                size="lg"
                selectionMode="single"
                placeholder={configData?.defaultDomain || 'Loading...'}
                selectedValue={selectedDomainId}
                onSelectionChange={(value: PrimitiveValue) => setSelectedDomainId(value as number | null)}
                className="w-full shadow-lg rounded-full border-0"
                inputRadius="rounded-full"
              >
                <Option value={null}>{configData?.defaultDomain || 'Default Domain'}</Option>
                {domainsData?.domains
                  ?.filter((domain: any) => {
                    const domainHost = domain.host.replace(/^https?:\/\//, '');
                    return domainHost !== configData?.defaultDomain;
                  })
                  ?.map((domain: any) => (
                    <Option key={domain.id} value={domain.id}>
                      {domain.host.replace(/^https?:\/\//, '')}
                    </Option>
                  )) || []}
              </Select>
            </div>
          )}

          {/* Buttons for Mobile */}
          <div className="w-full">
            {shortUrl ? (
              <div className="flex flex-col gap-3">
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="raised"
                    color="primary"
                    size="lg"
                    radius="rounded-full"
                    className="flex-1 cursor-pointer shadow-lg font-semibold"
                    onClick={() => window.open(shortUrl, '_blank')}
                  >
                    <OpenInNewIcon />
                    <span className="ml-2">{trans(message('Open'))}</span>
                  </Button>
                  <Button
                    variant="raised"
                    color="primary"
                    size="lg"
                    radius="rounded-full"
                    className="flex-1 cursor-pointer shadow-lg font-semibold"
                    onClick={openQrModal}
                  >
                    <QrCodeIcon />
                    <span className="ml-2">{trans(message('QR Code'))}</span>
                  </Button>
                </div>
                <Button
                  variant="raised"
                  color="primary"
                  size="lg"
                  radius="rounded-full"
                  className="w-full cursor-pointer shadow-lg font-semibold bg-primary-600 hover:bg-primary-700"
                  onClick={() => {
                    // Reset for new link
                    setShortUrl('');
                    setLongUrl('');
                    setSelectedDomainId(null);
                  }}
                >
                  {trans(message('New Link'))}
                </Button>
              </div>
            ) : (
              <Button
                variant="raised"
                color="primary"
                size="lg"
                radius="rounded-full"
                className="w-full cursor-pointer shadow-lg font-bold text-lg py-4 bg-primary-600 hover:bg-primary-700 text-white"
                disabled={createLink.isPending || !longUrl.trim()}
                onClick={() => createLink.mutate()}
              >
                {createLink.isPending ? (
                  <ProgressCircle size="sm" isIndeterminate />
                ) : (
                  trans(message('Shorten'))
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}

interface CreateLinkResponse extends BackendResponse {
  link: Link;
}
function postCreateLink(values: Partial<Link>): Promise<CreateLinkResponse> {
  return apiClient.post(`link`, values).then(r => r.data);
}

interface PublicDomainsResponse extends BackendResponse {
  domains: Array<{id: number; host: string}>;
}
function fetchPublicDomains(): Promise<PublicDomainsResponse> {
  return fetch('/api/public/domains').then(r => r.json());
}

function fetchAppConfig(): Promise<{defaultDomain: string; appUrl: string}> {
  return fetch('/api/public/config').then(r => r.json());
}
